import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  RefreshCw, 
  Plus, 
  FileText, 
  LogOut, 
  User, 
  Server, 
  ChevronDown,
  X,
  Search
} from 'lucide-react';
import InstanceTable, { Instance } from '../components/InstanceTable';
import api from '../api/axiosConfig';
import Footer from '../components/Footer';

// Google Analytics
const GA_MEASUREMENT_ID = 'G-56R16ZWNK6';
function gtag(...args: any[]) {
  (window as any).dataLayer.push(args);
}
function injectGtag() {
  if (!(window as any).gtagScriptInjected) {
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.async = true;
    document.head.appendChild(script);
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).gtag = gtag;
    gtag('js', new Date());
    gtag('config', GA_MEASUREMENT_ID);
    (window as any).gtagScriptInjected = true;
  }
}

// Search Input Component
const SearchInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}> = ({ value, onChange, placeholder = 'Search...' }) => (
  <div className="search-input-container">
    <Search size={18} className="search-icon" />
    <input
      type="text"
      className="search-input"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
    {value && (
      <button
        type="button"
        className="search-clear"
        onClick={() => onChange('')}
        aria-label="Clear search"
      >
        <X size={16} />
      </button>
    )}
  </div>
);

// Modal Component
const Modal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}> = ({ isOpen, onClose, title, subtitle, children, footer }) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-text">
            <h3 className="modal-title">{title}</h3>
            {subtitle && <p className="modal-subtitle">{subtitle}</p>}
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
};

const Home: React.FC = () => {
  const [instances, setInstances] = useState<Instance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    action: string;
    instance: Instance | null;
  }>({ open: false, action: '', instance: null });
  const [actionLoading, setActionLoading] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const [maintenanceMode] = useState(false);
  const [fatalError, setFatalError] = useState<string | null>(null);

  useEffect(() => {
    if (fatalError) {
      navigate('/error', { state: { message: fatalError } });
    }
  }, [fatalError, navigate]);

  useEffect(() => {
    injectGtag();
  }, []);

  const fetchInstances = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const jwt = localStorage.getItem('jwt');
      if (!jwt) throw new Error('Not authenticated');
      const response = await api.get('/api/listUserInstances', {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (response.data && typeof response.data === 'object' && response.data.ErrorMessage) {
        setFatalError(response.data.ErrorMessage);
        return;
      }
      if (!response.data || response.data.length === 0) {
        setError('no-instances');
        setInstances([]);
      } else {
        setInstances(response.data);
      }
    } catch (err: any) {
      if (err.response && err.response.status === 500) {
        localStorage.clear();
        navigate('/login');
        return;
      }
      let message =
        err.response?.data?.ErrorMessage ||
        err.response?.data?.message ||
        err.message ||
        'An unexpected error occurred.';
      if (typeof message === 'string' && message.startsWith('{') && message.endsWith('}')) {
        try {
          const parsed = JSON.parse(message);
          if (parsed.ErrorMessage) message = parsed.ErrorMessage;
        } catch {}
      }
      setFatalError(message);
      setInstances([]);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchInstances();
  }, [location.key, fetchInstances]);

  useEffect(() => {
    if (location.state && (location.state as any).reload) {
      fetchInstances();
      window.history.replaceState({}, document.title);
    }
  }, [location.state, fetchInstances]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchInstances();
    }, 300000); // 5 minutes
    return () => clearInterval(interval);
  }, [fetchInstances]);

  const actionApiMap: Record<string, string> = {
    restart: '/api/restartInstance',
    stop: '/api/stopInstance',
    terminate: '/api/terminateInstance',
  };

  const handleAction = (action: string, instance: Instance) => {
    setActionDialog({ open: true, action, instance });
  };

  const handleDialogClose = () => {
    setActionDialog({ open: false, action: '', instance: null });
  };

  const handleDialogConfirm = async () => {
    if (!actionDialog.instance) return;
    const { action, instance } = actionDialog;
    handleDialogClose();
    setActionLoading(true);
    try {
      const jwt = localStorage.getItem('jwt');
      if (!jwt) throw new Error('Not authenticated');
      const apiUrl = actionApiMap[action];
      const requestBody = {
        instance: action === 'terminate' ? { ...instance, status: 'terminated' } : instance,
      };
      await api.post(apiUrl, requestBody, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      fetchInstances();
    } catch {
      // Error handling silently
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogout = () => {
    try {
      localStorage.removeItem('jwt');
      localStorage.removeItem('email');
      localStorage.clear();
      navigate('/login');
    } catch (err) {
      try {
        navigate('/login');
      } catch {}
    }
  };

  // Filter instances based on search
  const filteredInstances = instances.filter((instance) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      instance.instance.toLowerCase().includes(query) ||
      (instance.description || '').toLowerCase().includes(query) ||
      instance.status.toLowerCase().includes(query) ||
      (instance.external_ip || '').toLowerCase().includes(query)
    );
  });

  if (maintenanceMode) {
    return (
      <div className="empty-state" style={{ minHeight: '100vh' }}>
        <div className="empty-state-icon">
          <Server size={64} />
        </div>
        <h3 className="empty-state-title">Site Maintenance Underway</h3>
        <p className="empty-state-description">
          We are currently performing scheduled maintenance. Please check back soon.
        </p>
      </div>
    );
  }

  return (
    <div className="instance-list-page">
      {/* Branded Header */}
      <header className="dashboard-header">
        <div className="dashboard-header-brand">
          <img src="/icon-60.png" alt="ecosystem.Ai" className="brand-logo-img" />
          <div className="brand-text">
            <h1 className="brand-title">Prediction as a Service</h1>
            <p className="brand-tagline">Powered by ecosystem.Ai</p>
          </div>
        </div>
        <div className="dashboard-header-actions">
          <div className="header-search">
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search instances..."
            />
          </div>
          <button
            className="header-action-btn"
            onClick={fetchInstances}
            disabled={loading}
            title="Refresh"
          >
            <RefreshCw size={14} className={loading ? 'spinning' : ''} />
          </button>
          <div style={{ position: 'relative' }}>
            <button
              className="header-action-btn"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              title="Account"
            >
              <User size={14} />
              <ChevronDown size={12} />
            </button>
            {userMenuOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '8px',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--border-radius)',
                  boxShadow: 'var(--shadow-lg)',
                  minWidth: '200px',
                  zIndex: 100,
                }}
              >
                <div
                  style={{
                    padding: 'var(--spacing-md)',
                    borderBottom: '1px solid var(--border-color)',
                    color: 'var(--text-muted)',
                    fontSize: '0.8125rem',
                  }}
                >
                  {localStorage.getItem('email')}
                </div>
                <button
                  onClick={() => navigate('/logs')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-sm)',
                    width: '100%',
                    padding: 'var(--spacing-md)',
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-secondary)',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--bg-hover)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <FileText size={16} />
                  View Logs
                </button>
                <button
                  onClick={handleLogout}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-sm)',
                    width: '100%',
                    padding: 'var(--spacing-md)',
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--danger)',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--danger-faded)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <LogOut size={16} />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Quick Access Section */}
      <section className="quick-access-section">
        <h2 className="quick-access-title">Quick Access</h2>
        <div className="quick-access-grid">
          <button
            className="quick-access-card quick-access-create"
            onClick={() => navigate('/create')}
          >
            <Plus size={24} />
            <span>Create New</span>
          </button>
          {instances.slice(0, 4).map((instance) => (
            <a
              key={instance.uuid}
              href={instance.external_ip || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="quick-access-card"
              style={{
                pointerEvents: instance.status === 'running' && instance.external_ip ? 'auto' : 'none',
                opacity: instance.status === 'running' ? 1 : 0.6,
              }}
            >
              <Server size={24} />
              <span title={instance.instance}>
                {instance.instance.length > 12
                  ? instance.instance.substring(0, 12) + '...'
                  : instance.instance}
              </span>
            </a>
          ))}
        </div>
      </section>

      {/* Section Header */}
      <div className="list-section-header">
        <div className="list-section-info">
          <span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
            Your Instances
          </span>
          <span className="toolbar-count">
            Showing {filteredInstances.length} of {instances.length} instances
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="list-content">
        {loading ? (
          <div className="loading-state">
            <RefreshCw size={32} className="spinning" />
            <span>Loading instances...</span>
          </div>
        ) : error === 'no-instances' || !instances || instances.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <Server size={64} />
            </div>
            <h3 className="empty-state-title">Ready to get started?</h3>
            <p className="empty-state-description">
              Create your first instance and enjoy the predictive capabilities of{' '}
              <strong style={{ color: 'var(--primary)' }}>ecosystem.Ai</strong>!
            </p>
            <div className="empty-state-action">
              <button className="primary-button accent" onClick={() => navigate('/create')}>
                <Plus size={18} />
                Create Instance
              </button>
            </div>
          </div>
        ) : (
          <InstanceTable
            instances={filteredInstances.map((inst) => ({
              ...inst,
              description: inst.description || '',
            }))}
            onRestart={(inst) => handleAction('restart', inst)}
            onStop={(inst) => handleAction('stop', inst)}
            onTerminate={(inst) => handleAction('terminate', inst)}
          />
        )}
      </div>

      <Footer />

      {/* Action Confirmation Modal */}
      <Modal
        isOpen={actionDialog.open}
        onClose={handleDialogClose}
        title={`Confirm ${actionDialog.action.charAt(0).toUpperCase() + actionDialog.action.slice(1)}`}
        subtitle="This action will affect your instance"
        footer={
          <div className="modal-footer-actions">
            <button className="secondary-button" onClick={handleDialogClose} disabled={actionLoading}>
              Cancel
            </button>
            <button
              className={`primary-button ${actionDialog.action === 'terminate' ? 'danger' : ''}`}
              onClick={handleDialogConfirm}
              disabled={actionLoading}
            >
              {actionLoading ? (
                <>
                  <RefreshCw size={16} className="spinning" />
                  Processing...
                </>
              ) : (
                `${actionDialog.action.charAt(0).toUpperCase() + actionDialog.action.slice(1)}`
              )}
            </button>
          </div>
        }
      >
        <p style={{ color: 'var(--text-secondary)' }}>
          Are you sure you want to {actionDialog.action}{' '}
          <strong style={{ color: 'var(--text-primary)' }}>{actionDialog.instance?.instance}</strong>?
        </p>
      </Modal>

      {/* Click outside to close user menu */}
      {userMenuOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
          }}
          onClick={() => setUserMenuOpen(false)}
        />
      )}
    </div>
  );
};

export default Home;
